import axiosInstance from "../../utils/axios";

export class StaffService {
 
  static async fetchProjectStaff() {
    try {
      const response = await axiosInstance.get("/staff/only_project");
      return response.data || [];
    } catch (error) {
      console.error("Error fetching project staff:", error);
      throw new Error(error.response?.data?.message || "Failed to fetch project staff");
    }
  }

  static async fetchOrgStaff() {
    try {
      const response = await axiosInstance.get("/staff/");
      return response.data || [];
    } catch (error) {
      console.error("Error fetching org staff:", error);
      throw new Error(error.response?.data?.message || "Failed to fetch organization staff");
    }
  }

  static async createStaff(staffData, isOrgOnly = false) {
    try {
      const endpoint = isOrgOnly ? "/staff/create?only_org=True" : "/staff/create";
      const response = await axiosInstance.post(endpoint, staffData);
      return response.data;
    } catch (error) {
      console.error("Error creating staff:", error);
      throw new Error(error.response?.data?.message || "Failed to create staff");
    }
  }

  static async updateStaff(staffId, staffData) {
    try {
      let response;
      
      try {
        response = await axiosInstance.post(`/staff/update?id=${staffId}`, staffData);
      } catch (error) {
        if (error.response?.status === 405) {
          try {
            response = await axiosInstance.put(`/staff/update?id=${staffId}`, staffData);
          } catch (putError) {
            try {
              response = await axiosInstance.post(`/staff/update/${staffId}`, staffData);
            } catch (postPathError) {
              const payloadWithId = { ...staffData, id: staffId };
              response = await axiosInstance.post("/staff/update", payloadWithId);
            }
          }
        } else {
          throw error;
        }
      }
      
      return response.data;
    } catch (error) {
      console.error("Error updating staff:", error);
      throw new Error(error.response?.data?.message || "Failed to update staff");
    }
  }

  /**
   * Delete staff member
   */
  static async deleteStaff(staffId, forceDelete = false) {
    try {
      const forceParam = forceDelete ? "force=True" : "";
      const url = `/staff/delete?id=${staffId}${forceParam ? `&${forceParam}` : ""}`;
      
      const response = await axiosInstance.delete(url);
      return response.data;
    } catch (error) {
      console.error("Error deleting staff:", error);
      throw new Error(error.response?.data?.message || "Failed to delete staff");
    }
  }

  /**
   * Deallocate staff from project
   */
  static async deallocateStaff(staffId, forceDelete = false) {
    try {
      const url = `/staff/deallocateToProject${forceDelete ? "?force=True" : ""}`;
      const payload = { id: staffId };
      
      const response = await axiosInstance.post(url, payload);
      return response.data;
    } catch (error) {
      console.error("Error deallocating staff:", error);
      throw new Error(error.response?.data?.message || "Failed to deallocate staff");
    }
  }

  /**
   * Import staff to project
   */
  static async importStaff(staffIds) {
    try {
      const payload = Array.isArray(staffIds) ? staffIds : [staffIds];
      const response = await axiosInstance.post("/staff/importToProject", payload);
      return response.data;
    } catch (error) {
      console.error("Error importing staff:", error);
      throw new Error(error.response?.data?.message || "Failed to import staff");
    }
  }

  /**
   * Process staff profile images and documents
   */
  static async processProfileImages(staffList) {
    if (!Array.isArray(staffList)) {
      return [];
    }

    for (const staff of staffList) {
      if (!staff.user || !staff.user.profile_data) continue;

      // Process profile URL
      if (!staff.user.profile_data.profile_url) {
        let profileImage = {};
        if (staff.user.profile_data.other_documents) {
          profileImage = staff.user.profile_data.other_documents.find(
            (doc) => doc.type === 3 && doc.upload_id
          ) || {};
        }

        if (profileImage?.upload_id) {
          try {
            const profileResponse = await axiosInstance.get(
              `/file_upload/download?id=${profileImage.upload_id}&org=True`
            );
            staff.user.profile_data.profile_url = profileResponse.data;
          } catch (error) {
            console.error("Error fetching profile image:", error);
            staff.user.profile_data.profile_url = null;
          }
        } else {
          staff.user.profile_data.profile_url = null;
        }
      }

      // Process photo ID URL
      if (staff.user.profile_data.photo_id && !staff.user.profile_data.photo_url) {
        try {
          const photoResponse = await axiosInstance.get(
            `/file_upload/download?id=${staff.user.profile_data.photo_id}&org=True`
          );
          staff.user.profile_data.photo_url = photoResponse.data;
        } catch (error) {
          console.error("Error fetching photo ID:", error);
          staff.user.profile_data.photo_url = null;
        }
      }
    }
    
    return staffList;
  }

  /**
   * Get file URL for download/preview
   */
  static async getFileUrl(fileId, isOrg = true) {
    try {
      const response = await axiosInstance.get(
        `/file_upload/download?id=${fileId}&org=${isOrg}`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching file URL for ${fileId}:`, error);
      throw new Error("Failed to fetch file URL");
    }
  }

  /**
   * Upload single file
   */
  static async uploadSingleFile(file, isOrg = true) {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axiosInstance.post(
        `/file_upload/upload/image?org=${isOrg}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 30000,
        }
      );

      return {
        id: response.data,
        name: file.name,
        type: 1,
      };
    } catch (error) {
      console.error("Error uploading file:", error);
      throw new Error(error.response?.data?.message || "Failed to upload file");
    }
  }

  /**
   * Upload multiple files
   */
  static async uploadMultipleFiles(files, isOrg = true) {
    const results = {
      successful: [],
      failed: [],
    };

    for (const file of files) {
      try {
        const result = await this.uploadSingleFile(file, isOrg);
        results.successful.push(result);
      } catch (error) {
        results.failed.push({
          file: file.name,
          error: error.message,
        });
      }
    }

    return results;
  }

  /**
   * Delete file
   */
  static async deleteFile(fileId, isOrg = true) {
    try {
      const response = await axiosInstance.delete(
        `/file_upload/delete?id=${fileId}&org=${isOrg}`
      );
      return response.data;
    } catch (error) {
      console.error(`Error deleting file ${fileId}:`, error);
      throw new Error("Failed to delete file");
    }
  }
}